import { useEffect, useState, useMemo } from "react";
import { Save, GripVertical, Eye, EyeOff, RotateCcw } from "lucide-react";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";

const DEFAULTS = {
  site: [
    { to: "/admin", label: "Dashboard" },
    { to: "/admin/settings", label: "Configuración" },
    { to: "/admin/appearance", label: "Apariencia" },
    { to: "/admin/about", label: "Quiénes Somos" },
    { to: "/admin/services", label: "Servicios (web)" },
    { to: "/admin/talents", label: "Talentos (web)" },
    { to: "/admin/products", label: "Productos (web)" },
    { to: "/admin/clients", label: "Clientes (logos)" },
    { to: "/admin/media", label: "Medios" },
  ],
  commercial: [
    { to: "/admin/comercial", label: "Dashboard Comercial" },
    { to: "/admin/comercial/clientes", label: "Clientes" },
    { to: "/admin/comercial/marcas", label: "Marcas" },
    { to: "/admin/comercial/agencias", label: "Agencias" },
    { to: "/admin/comercial/contactos", label: "Contactos / Ejecutivos" },
    { to: "/admin/leads", label: "Contactos web (Leads)" },
    { to: "/admin/comercial/categorias", label: "Categorías" },
    { to: "/admin/comercial/productos-com", label: "Productos comerciales" },
    { to: "/admin/comercial/paquetes", label: "Paquetes" },
    { to: "/admin/comercial/talentos", label: "Talentos comerciales" },
    { to: "/admin/proposals", label: "Propuestas" },
    { to: "/admin/proposal-themes", label: "Plantillas Propuesta" },
    { to: "/admin/comercial/workflow", label: "Workflow" },
    { to: "/admin/comercial/campanas", label: "Campañas" },
  ],
  accounting: [
    { to: "/admin/contabilidad", label: "Dashboard Contable" },
    { to: "/admin/comercial/cobranza", label: "Órdenes de cobro" },
    { to: "/admin/comercial/cuentas-por-cobrar", label: "Cuentas por cobrar" },
    { to: "/admin/comercial/reportes", label: "Reportes contables" },
  ],
  production: [
    { to: "/admin/comercial/programas", label: "Programas streaming" },
    { to: "/admin/produccion/acciones", label: "Acciones Comerciales" },
    { to: "/admin/contenido", label: "Contenido (herramientas)" },
  ],
  system: [
    { to: "/admin/historial", label: "Historial" },
    { to: "/admin/users", label: "Usuarios" },
    { to: "/admin/menu-config", label: "Menú admin" },
    { to: "/admin/account", label: "Mi cuenta" },
  ],
};

function withOverrides(items, overrides) {
  return items
    .map((it, i) => ({ ...it, _idx: i, _ov: overrides[it.to] || {} }))
    .sort((a, b) => {
      const ao = a._ov.order != null ? a._ov.order : a._idx;
      const bo = b._ov.order != null ? b._ov.order : b._idx;
      return ao - bo;
    });
}

export default function AdminMenuConfig() {
  const { setConfig: setGlobal } = useSite();
  const [cfg, setCfg] = useState(null);
  const [overrides, setOverrides] = useState({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    api.get("/site-config").then((c) => {
      setCfg(c);
      setOverrides(c.admin_menu_overrides || {});
    });
  }, []);

  const save = async () => {
    const body = { ...cfg, admin_menu_overrides: overrides };
    const updated = await api.put("/site-config", body);
    setGlobal(updated);
    setCfg(updated);
    setOverrides(updated.admin_menu_overrides || {});
    setSaved(true); setTimeout(() => setSaved(false), 2000);
  };

  const reset = () => { setOverrides({}); };

  const updateMod = (key, patch) => {
    setOverrides((o) => ({ ...o, [key]: { ...(o[key] || {}), ...patch } }));
  };

  const reorderGroup = (group, fromIdx, toIdx) => {
    if (fromIdx === toIdx) return;
    const items = withOverrides(DEFAULTS[group], overrides);
    const [moved] = items.splice(fromIdx, 1);
    items.splice(toIdx, 0, moved);
    // Reassign sequential orders
    const next = { ...overrides };
    items.forEach((it, i) => {
      next[it.to] = { ...(next[it.to] || {}), order: i };
    });
    setOverrides(next);
  };

  if (!cfg) return <div className="text-muted-foreground">Cargando...</div>;

  return (
    <div data-testid="admin-menu-config">
      <div className="flex items-center justify-between mb-8 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-4xl font-black tracking-tighter mb-1">Menú admin</h1>
          <p className="text-sm text-muted-foreground">Arrastrá ítems para reordenar, renombrá o ocultá. Las flechitas también funcionan.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={reset} className="inline-flex items-center gap-2 px-4 h-10 border border-border rounded-sm text-sm font-semibold hover:border-primary"><RotateCcw className="w-4 h-4" /> Restablecer</button>
          <button onClick={save} data-testid="save-menu-config" className="inline-flex items-center gap-2 px-5 h-10 bg-primary text-primary-foreground rounded-sm text-sm font-semibold"><Save className="w-4 h-4" /> {saved ? "Guardado" : "Guardar"}</button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {Object.entries(DEFAULTS).map(([group, _items]) => (
          <Group key={group} title={titleOf(group)} group={group} overrides={overrides} updateMod={updateMod} reorderGroup={reorderGroup} />
        ))}
      </div>

      <p className="text-xs text-muted-foreground mt-6">Tip: arrastrá un ítem por el ícono <GripVertical className="inline w-3 h-3" /> hacia arriba o abajo para reordenar dentro del grupo.</p>
    </div>
  );
}

const titleOf = (k) => ({ site: "Sitio público", commercial: "Comercial", accounting: "Contabilidad", production: "Producción y Contenido", system: "Sistema" }[k] || k);

function Group({ title, group, overrides, updateMod, reorderGroup }) {
  const items = useMemo(() => withOverrides(DEFAULTS[group], overrides), [group, overrides]);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);

  return (
    <section className="border border-border rounded-sm p-4">
      <h3 className="font-display font-bold uppercase tracking-widest text-xs text-muted-foreground mb-3">{title}</h3>
      <ul className="space-y-1" data-testid={`group-${group}`}>
        {items.map((it, idx) => {
          const ov = overrides[it.to] || {};
          const hidden = !!ov.hidden;
          const isOver = overIdx === idx && dragIdx !== null && dragIdx !== idx;
          return (
            <li
              key={it.to}
              draggable
              onDragStart={(e) => { setDragIdx(idx); e.dataTransfer.effectAllowed = "move"; }}
              onDragOver={(e) => { e.preventDefault(); setOverIdx(idx); }}
              onDragLeave={() => setOverIdx((cur) => (cur === idx ? null : cur))}
              onDrop={(e) => { e.preventDefault(); if (dragIdx !== null) reorderGroup(group, dragIdx, idx); setDragIdx(null); setOverIdx(null); }}
              onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
              data-testid={`menu-item-${it.to.replace(/\//g, "-")}`}
              className={`flex items-center gap-2 p-2 border rounded-sm transition-all ${hidden ? "opacity-50 border-dashed" : "border-border"} ${dragIdx === idx ? "opacity-30" : ""} ${isOver ? "border-primary border-2 bg-primary/5" : ""}`}
            >
              <button
                type="button"
                onClick={() => idx > 0 && reorderGroup(group, idx, idx - 1)}
                disabled={idx === 0}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30 px-1 leading-none"
                data-testid={`up-${it.to.replace(/\//g, "-")}`}
              >▲</button>
              <button
                type="button"
                onClick={() => idx < items.length - 1 && reorderGroup(group, idx, idx + 1)}
                disabled={idx === items.length - 1}
                className="text-muted-foreground hover:text-foreground disabled:opacity-30 px-1 leading-none"
                data-testid={`down-${it.to.replace(/\//g, "-")}`}
              >▼</button>
              <GripVertical className="w-4 h-4 text-muted-foreground cursor-grab active:cursor-grabbing" />
              <input
                value={ov.label || it.label}
                onChange={(e) => updateMod(it.to, { label: e.target.value })}
                placeholder={it.label}
                className="flex-1 h-8 px-2 bg-background border border-border rounded-sm text-sm focus:border-primary outline-none"
              />
              <button
                onClick={() => updateMod(it.to, { hidden: !hidden })}
                className={`p-1.5 border rounded-sm ${hidden ? "border-amber-500 text-amber-500" : "border-border hover:border-primary"}`}
                title={hidden ? "Mostrar" : "Ocultar"}
              >
                {hidden ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
